import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShoppingBag, Plus, MessageCircle, MapPin, Trash2, CheckCircle, Phone, ImagePlus, X, ChevronRight, ChevronLeft, Tag, Bell, Eye, Star } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import ChatConversation from '../components/ChatConversation';
import Navbar from '../components/Navbar';

const CATEGORIES = ['מחבטים', 'נעליים', 'תיקים', 'גידים למחבט', 'ביגוד', 'אחר'];
const CONDITIONS = ['חדש', 'כמו חדש', 'משומש'];
const CAT_EMOJI = { 'מחבטים': '🎾', 'נעליים': '👟', 'תיקים': '🎒', 'גידים למחבט': '🧵', 'ביגוד': '👕', 'אחר': '📦' };
const HOT_VIEWS = 10;
const REVIEW_TAGS = ['אמין', 'הוגן', 'זמין', 'תקשורת מעולה', 'מוצר כמתואר', 'מומלץ'];
const TAG_EMOJI = { 'אמין': '🤝', 'הוגן': '💰', 'זמין': '⚡', 'תקשורת מעולה': '💬', 'מוצר כמתואר': '✅', 'מומלץ': '⭐' };

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1000;
        let { width, height } = img;
        if (width > max || height > max) {
          const ratio = Math.min(max / width, max / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function GearBoardPage() {
  const { API, user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('הכל');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ title: '', category: '', condition: '', price: '', area: '', description: '', phone: '', images: [] });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [chatUser, setChatUser] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertCats, setAlertCats] = useState([]);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [sellerReviews, setSellerReviews] = useState({ reviews: [], summary: {}, total: 0 });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTags, setReviewTags] = useState([]);
  const [reviewComment, setReviewComment] = useState('');
  const [savingReview, setSavingReview] = useState(false);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/gear-board`);
      setPosts(res.data.posts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const openAlerts = async () => {
    setAlertsOpen(true);
    try {
      const res = await axios.get(`${API}/gear-alerts`, authHeaders);
      setAlertCats(res.data.categories || []);
    } catch { /* ignore */ }
  };

  const saveAlerts = async () => {
    setSavingAlerts(true);
    try {
      await axios.put(`${API}/gear-alerts`, { categories: alertCats }, authHeaders);
      toast.success(alertCats.length ? 'ההתראות נשמרו! 🔔' : 'ההתראות בוטלו');
      setAlertsOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בשמירה');
    } finally {
      setSavingAlerts(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/gear-board/${id}`);
      setImgIdx(0);
      setDetail(res.data);
      const isOwner = user && res.data.user_id === user.id;
      if (!isOwner && !sessionStorage.getItem(`gearViewed_${id}`)) {
        sessionStorage.setItem(`gearViewed_${id}`, '1');
        axios.post(`${API}/gear-board/${id}/view`).catch(() => {});
      }
      fetchSellerReviews(res.data.user_id);
    } catch (e) {
      toast.error('שגיאה בטעינת המודעה');
    }
  };

  const fetchSellerReviews = async (sellerId) => {
    try {
      const res = await axios.get(`${API}/seller-reviews/${sellerId}`);
      setSellerReviews(res.data);
    } catch {
      setSellerReviews({ reviews: [], summary: {}, total: 0 });
    }
  };

  const openReview = () => {
    const mineReview = sellerReviews.reviews.find((r) => r.reviewer_id === user?.id);
    setReviewTags(mineReview?.tags || []);
    setReviewComment(mineReview?.comment || '');
    setReviewOpen(true);
  };

  const submitReview = async () => {
    if (reviewTags.length === 0) { toast.error('יש לבחור לפחות תכונה אחת'); return; }
    setSavingReview(true);
    try {
      await axios.post(`${API}/seller-reviews/${detail.user_id}`, { tags: reviewTags, comment: reviewComment }, authHeaders);
      toast.success('הביקורת נשמרה! ⭐');
      setReviewOpen(false);
      fetchSellerReviews(detail.user_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בשמירת הביקורת');
    } finally {
      setSavingReview(false);
    }
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.images.length + files.length > 5) { toast.error('עד 5 תמונות למודעה'); return; }
    try {
      const compressed = await Promise.all(files.map(compressImage));
      setForm((f) => ({ ...f, images: [...f.images, ...compressed].slice(0, 5) }));
    } catch {
      toast.error('שגיאה בטעינת התמונה');
    }
    e.target.value = '';
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('יש להזין שם מוצר'); return; }
    if (!form.category) { toast.error('יש לבחור קטגוריה'); return; }
    if (!form.condition) { toast.error('יש לבחור מצב מוצר'); return; }
    if (form.price === '' || isNaN(Number(form.price)) || Number(form.price) < 0) { toast.error('יש להזין מחיר תקין'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/gear-board`, { ...form, price: Number(form.price) }, authHeaders);
      toast.success('המודעה פורסמה! 🛒');
      setFormOpen(false);
      setForm({ title: '', category: '', condition: '', price: '', area: '', description: '', phone: '', images: [] });
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בפרסום');
    } finally {
      setSaving(false);
    }
  };

  const toggleSold = async (id) => {
    try {
      const res = await axios.patch(`${API}/gear-board/${id}/sold`, {}, authHeaders);
      toast.success(res.data.sold ? 'המודעה סומנה כנמכר 🎉' : 'המודעה חזרה להיות פעילה');
      setDetail(null);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק את המודעה?')) return;
    try {
      await axios.delete(`${API}/gear-board/${id}`, authHeaders);
      toast.success('המודעה נמחקה');
      setDetail(null);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה');
    }
  };

  const timeAgo = (iso) => {
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `לפני ${Math.max(1, mins)} דק׳`;
    if (mins < 1440) return `לפני ${Math.floor(mins / 60)} שע׳`;
    return `לפני ${Math.floor(mins / 1440)} ימים`;
  };

  const waLink = (phone, title) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return null;
    const intl = digits.startsWith('972') ? digits : digits.startsWith('0') ? '972' + digits.slice(1) : digits;
    return `https://wa.me/${intl}?text=${encodeURIComponent(`היי, ראיתי את המודעה "${title}" בלוח הציוד 🎾`)}`;
  };

  const filtered = catFilter === 'הכל' ? posts : posts.filter((p) => p.category === catFilter);
  const activePosts = filtered.filter((p) => !p.sold);
  const soldPosts = filtered.filter((p) => p.sold);

  const condBadge = (c) =>
    c === 'חדש' ? 'bg-emerald-500 text-white' : c === 'כמו חדש' ? 'bg-blue-500 text-white' : 'bg-amber-500 text-white';

  const GearCard = ({ p }) => (
    <Card
      onClick={() => openDetail(p.id)}
      className={`cursor-pointer overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 ${p.sold ? 'opacity-60' : ''}`}
      data-testid={`gear-post-${p.id}`}
    >
      <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center">
        {p.images && p.images[0] ? (
          <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-5xl">{CAT_EMOJI[p.category] || '📦'}</span>
        )}
        {p.sold && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Badge className="bg-red-500 text-white text-base px-4 py-1">נמכר ✔</Badge>
          </div>
        )}
        {p.image_count > 1 && (
          <Badge className="absolute bottom-2 left-2 bg-black/60 text-white text-xs">📷 {p.image_count}</Badge>
        )}
        {!p.sold && (p.views || 0) >= HOT_VIEWS && (
          <Badge className="absolute top-2 left-2 bg-red-500 text-white animate-pulse" data-testid={`gear-hot-${p.id}`}>🔥 מבוקש</Badge>
        )}
        <Badge className={`absolute top-2 right-2 ${condBadge(p.condition)}`}>{p.condition}</Badge>
      </div>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-gray-800 dark:text-gray-100 text-sm leading-tight line-clamp-2">{p.title}</h3>
          <span className="font-black text-emerald-600 whitespace-nowrap">{p.price === 0 ? 'חינם' : `₪${p.price}`}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-2" data-testid={`gear-seller-${p.id}`}>
          <Avatar className="w-6 h-6 border border-orange-200">
            {p.user_avatar && <AvatarImage src={p.user_avatar} alt={p.user_name} />}
            <AvatarFallback className="bg-orange-100 text-orange-700 text-[10px] font-bold">{(p.user_name || '?').charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-xs font-bold text-gray-600 dark:text-gray-300 truncate">{p.user_name}</span>
        </div>
        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400 flex-wrap">
          <span>{CAT_EMOJI[p.category]} {p.category}</span>
          {p.area && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.area}</span>}
          <span className="flex items-center gap-0.5" data-testid={`gear-views-${p.id}`}><Eye className="w-3 h-3" />{p.views || 0}</span>
          <span className="mr-auto">{timeAgo(p.created_at)}</span>
        </div>
      </CardContent>
    </Card>
  );

  const mine = detail && user && detail.user_id === user.id;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/50 to-white dark:from-slate-900 dark:to-slate-950" dir="rtl" data-testid="gear-board-page">
      <Navbar />
      <div className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 rounded-full px-4 py-1.5 text-sm font-bold mb-3">
              <ShoppingBag className="w-4 h-4" />
              קונים ומוכרים בקהילה
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white mb-2">🛒 לוח ציוד</h1>
            <p className="text-gray-500">ציוד טניס יד שנייה וחדש — פרסם מודעה או צור קשר עם המוכר</p>
          </div>

          <div className="flex justify-center gap-3 flex-wrap mb-6">
            {user ? (
              <>
                <Button onClick={() => setFormOpen(true)} className="bg-gradient-to-l from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 rounded-full px-8 py-6 text-lg font-bold shadow-lg" data-testid="new-gear-btn">
                  <Plus className="w-5 h-5 ml-2" />
                  פרסם מודעה
                </Button>
                <Button variant="outline" onClick={openAlerts} className="rounded-full px-6 py-6 font-bold border-2 border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-slate-800" data-testid="gear-alerts-btn">
                  <Bell className="w-5 h-5 ml-2" />
                  התראות ציוד
                </Button>
              </>
            ) : (
              <Link to="/auth"><Button variant="outline" className="rounded-full px-8">התחבר כדי לפרסם מודעה</Button></Link>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pb-2 mb-6 justify-center" data-testid="gear-category-filters">
            {['הכל', ...CATEGORIES].map((c) => (
              <button
                key={c}
                onClick={() => setCatFilter(c)}
                data-testid={`gear-filter-${c}`}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-bold transition-colors border ${
                  catFilter === c
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-orange-300'
                }`}
              >
                {c === 'הכל' ? '✨ הכל' : `${CAT_EMOJI[c]} ${c}`}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-gray-400 py-12">טוען...</p>
          ) : activePosts.length === 0 && soldPosts.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <span className="text-5xl block mb-3">🛒</span>
              אין מודעות בקטגוריה זו — היה הראשון לפרסם!
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {activePosts.map((p) => <GearCard key={p.id} p={p} />)}
              </div>
              {soldPosts.length > 0 && (
                <>
                  <h3 className="text-sm font-bold text-gray-400 pt-8 pb-3">🎉 נמכרו לאחרונה</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {soldPosts.map((p) => <GearCard key={p.id} p={p} />)}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* התראות ציוד */}
        <Dialog open={alertsOpen} onOpenChange={setAlertsOpen}>
          <DialogContent className="sm:max-w-sm" dir="rtl" data-testid="gear-alerts-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl">🔔 התראות ציוד חדש</DialogTitle>
              <DialogDescription>בחר קטגוריות — נודיע לך ברגע שעולה מודעה מתאימה</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {CATEGORIES.map((c) => (
                <label key={c} className="flex items-center gap-3 cursor-pointer rounded-xl border p-3 hover:border-orange-300 transition-colors" data-testid={`gear-alert-row-${c}`}>
                  <Checkbox
                    checked={alertCats.includes(c)}
                    onCheckedChange={(v) => setAlertCats((prev) => v ? [...prev, c] : prev.filter((x) => x !== c))}
                    data-testid={`gear-alert-checkbox-${c}`}
                  />
                  <span className="font-bold text-gray-700 dark:text-gray-200">{CAT_EMOJI[c]} {c}</span>
                </label>
              ))}
              <Button onClick={saveAlerts} disabled={savingAlerts} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-5 font-bold" data-testid="save-gear-alerts-btn">
                {savingAlerts ? 'שומר...' : 'שמור התראות 🔔'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* טופס מודעה חדשה */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-xl">🛒 מודעה חדשה</DialogTitle>
              <DialogDescription>פרסם ציוד למכירה — חדש או יד שנייה</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="mb-1.5 block">שם המוצר *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="לדוגמה: מחבט Wilson Blade 98" maxLength={80} data-testid="gear-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">קטגוריה *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="gear-category-select"><SelectValue placeholder="בחר" /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CAT_EMOJI[c]} {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">מצב *</Label>
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                    <SelectTrigger data-testid="gear-condition-select"><SelectValue placeholder="בחר" /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">מחיר (₪) *</Label>
                <Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0 = חינם" data-testid="gear-price-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">תמונות (עד 5)</Label>
                <div className="flex gap-2 flex-wrap">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                        className="absolute top-0 left-0 bg-red-500 text-white rounded-br-lg p-0.5"
                        data-testid={`remove-image-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {form.images.length < 5 && (
                    <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-orange-400 text-gray-400" data-testid="add-image-label">
                      <ImagePlus className="w-6 h-6" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleImages} data-testid="gear-image-input" />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">תיאור</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="פרטים על המוצר, סיבת מכירה וכו׳" rows={3} maxLength={1000} data-testid="gear-description-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">אזור מסירה</Label>
                <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="לדוגמה: ראשון לציון" data-testid="gear-area-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">טלפון לווצאפ (לא חובה)</Label>
                <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="לדוגמה: 050-1234567" dir="ltr" className="text-right" data-testid="gear-phone-input" />
                <p className="text-xs text-gray-400 mt-1">אם תוסיף - יופיע כפתור ווצאפ ירוק על המודעה</p>
              </div>
              <Button onClick={handleCreate} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-5 font-bold" data-testid="submit-gear-btn">
                {saving ? 'מפרסם...' : 'פרסם מודעה 🛒'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* פרטי מודעה */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="gear-detail-dialog">
            {detail && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl flex items-center gap-2 flex-wrap">
                    {detail.title}
                    {detail.sold && <Badge className="bg-red-500 text-white">נמכר ✔</Badge>}
                    {!detail.sold && (detail.views || 0) >= HOT_VIEWS && <Badge className="bg-red-500 text-white animate-pulse">🔥 מבוקש</Badge>}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500">
                    <Badge className={condBadge(detail.condition)}>{detail.condition}</Badge>
                    <Badge variant="outline"><Tag className="w-3 h-3 ml-1" />{CAT_EMOJI[detail.category]} {detail.category}</Badge>
                    <span className="text-xs">{timeAgo(detail.created_at)}</span>
                </div>

                {detail.images && detail.images.length > 0 && (
                  <div className="relative rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img src={detail.images[imgIdx]} alt={detail.title} className="w-full max-h-72 object-contain" data-testid="gear-detail-image" />
                    {detail.images.length > 1 && (
                      <>
                        <button onClick={() => setImgIdx((imgIdx + 1) % detail.images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5" data-testid="gear-img-next">
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <button onClick={() => setImgIdx((imgIdx - 1 + detail.images.length) % detail.images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5" data-testid="gear-img-prev">
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {detail.images.map((_, i) => (
                            <span key={i} className={`w-2 h-2 rounded-full ${i === imgIdx ? 'bg-white' : 'bg-white/40'}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="text-3xl font-black text-emerald-600" data-testid="gear-detail-price">{detail.price === 0 ? 'חינם 🎁' : `₪${detail.price}`}</div>

                {mine && (
                  <div className="flex items-center gap-2 bg-orange-50 dark:bg-slate-800 border border-orange-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-orange-700 dark:text-orange-300 font-bold text-sm" data-testid="gear-owner-views">
                    <Eye className="w-4 h-4" />
                    {detail.views || 0} אנשים צפו במודעה שלך 👀
                  </div>
                )}
                {!mine && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400" data-testid="gear-detail-views">
                    <Eye className="w-3.5 h-3.5" />
                    {detail.views || 0} צפיות
                  </div>
                )}

                {detail.description && <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{detail.description}</p>}

                <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                  <Link to={`/profile/${detail.user_id}`} className="flex items-center gap-2 font-bold text-gray-700 dark:text-gray-200 hover:text-orange-600" data-testid="gear-detail-seller">
                    <Avatar className="w-9 h-9 border-2 border-orange-200">
                      {detail.user_avatar && <AvatarImage src={detail.user_avatar} alt={detail.user_name} />}
                      <AvatarFallback className="bg-orange-100 text-orange-700 font-bold">{(detail.user_name || '?').charAt(0)}</AvatarFallback>
                    </Avatar>
                    {detail.user_name}
                  </Link>
                  {detail.area && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-orange-500" />{detail.area}</span>}
                </div>

                {/* ביקורות על המוכר */}
                <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-3 space-y-2" data-testid="seller-reviews-section">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                      ⭐ ביקורות על המוכר {sellerReviews.total > 0 && <span className="text-gray-400 font-normal">({sellerReviews.total})</span>}
                    </span>
                    {user && !mine && (
                      <Button size="sm" variant="outline" onClick={openReview} className="rounded-full text-amber-600 border-amber-300 h-7 text-xs" data-testid="rate-seller-btn">
                        <Star className="w-3.5 h-3.5 ml-1" />
                        דרג את המוכר
                      </Button>
                    )}
                  </div>
                  {sellerReviews.total === 0 ? (
                    <p className="text-xs text-gray-400">עדיין אין ביקורות על המוכר</p>
                  ) : (
                    <>
                      <div className="flex gap-1.5 flex-wrap">
                        {Object.entries(sellerReviews.summary).sort((a, b) => b[1] - a[1]).map(([tag, count]) => (
                          <Badge key={tag} variant="outline" className="text-emerald-700 dark:text-emerald-300 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30" data-testid={`seller-tag-${tag}`}>
                            {TAG_EMOJI[tag]} {tag} ×{count}
                          </Badge>
                        ))}
                      </div>
                      {sellerReviews.reviews.filter((r) => r.comment).slice(0, 3).map((r) => (
                        <div key={r.id} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 pt-1">
                          <Avatar className="w-5 h-5">
                            {r.reviewer_avatar && <AvatarImage src={r.reviewer_avatar} alt={r.reviewer_name} />}
                            <AvatarFallback className="text-[9px] bg-slate-200 dark:bg-slate-700">{(r.reviewer_name || '?').charAt(0)}</AvatarFallback>
                          </Avatar>
                          <p><span className="font-bold">{r.reviewer_name}:</span> {r.comment}</p>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {!mine && !detail.sold && user && (
                    <Button size="sm" onClick={() => { setChatUser({ id: detail.user_id, name: detail.user_name }); setDetail(null); }} className="bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid="gear-chat-btn">
                      <MessageCircle className="w-4 h-4 ml-1" />
                      💬 שלח הודעה
                    </Button>
                  )}
                  {!mine && !detail.sold && detail.phone && waLink(detail.phone, detail.title) && (
                    <a href={waLink(detail.phone, detail.title)} target="_blank" rel="noopener noreferrer" data-testid="gear-whatsapp-btn">
                      <Button size="sm" className="bg-[#25D366] hover:bg-[#1ebe5b] rounded-full">
                        <Phone className="w-4 h-4 ml-1" />
                        ווצאפ
                      </Button>
                    </a>
                  )}
                  {!user && !detail.sold && (
                    <Link to="/auth"><Button size="sm" variant="outline" className="rounded-full">התחבר כדי ליצור קשר</Button></Link>
                  )}
                  {mine && (
                    <Button size="sm" variant="outline" onClick={() => toggleSold(detail.id)} className="rounded-full text-emerald-600 border-emerald-300" data-testid="gear-sold-btn">
                      <CheckCircle className="w-4 h-4 ml-1" />
                      {detail.sold ? 'החזר לפעיל' : 'סמן כנמכר'}
                    </Button>
                  )}
                  {(mine || user?.is_admin) && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(detail.id)} className="text-red-500 hover:text-red-600 rounded-full" data-testid="gear-delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* דירוג מוכר */}
        <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
          <DialogContent className="sm:max-w-sm" dir="rtl" data-testid="seller-review-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl">⭐ דרג את {detail?.user_name}</DialogTitle>
              <DialogDescription>איך הייתה החוויה שלך עם המוכר?</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                {REVIEW_TAGS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setReviewTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t])}
                    data-testid={`review-tag-${t}`}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-bold border transition-colors ${
                      reviewTags.includes(t)
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-amber-300'
                    }`}
                  >
                    {TAG_EMOJI[t]} {t}
                  </button>
                ))}
              </div>
              <Textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="מילה טובה על המוכר (לא חובה)"
                rows={2}
                maxLength={200}
                data-testid="review-comment-input"
              />
              <Button onClick={submitReview} disabled={savingReview} className="w-full bg-amber-500 hover:bg-amber-600 rounded-full py-5 font-bold" data-testid="submit-review-btn">
                {savingReview ? 'שומר...' : 'שמור ביקורת ⭐'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {chatUser && (
          <ChatConversation
            open={!!chatUser}
            onClose={() => setChatUser(null)}
            otherUserId={chatUser.id}
            otherUserName={chatUser.name}
          />
        )}
      </div>
    </div>
  );
}
